
/**
 * Authentication Context Provider for One Voice
 *
 * This module provides React context for managing user authentication state
 * throughout the application. It handles:
 *   - Login/logout flows with JWT token management
 *   - Persistent authentication via localStorage token
 *   - User state hydration on page load
 *   - Protected route support via useAuth hook
 *
 * Architecture:
 *   1. On mount, checks for existing token in localStorage
 *   2. If token exists, fetches user data from /auth/me
 *   3. Provides authentication state to all child components
 *   4. Handles token refresh and logout cleanup
 *
 * Usage:
 *   // In _app.tsx or layout.tsx
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 *
 *   // In any component
 *   const { user, login, logout, isAuthenticated } = useAuth();
 *
 * @module lib/auth-context
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, AuthResponse } from '@/types';
import { api, setAuthToken, removeAuthToken, setAuthOperationInProgress, setAuthLoading } from '@/lib/api';
import { logger } from '@/lib/logger';

/** Interval to call POST /auth/refresh (ms). Backend extends session with new token. */
const TOKEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes (token typically 30 min)

/**
 * Shape of the authentication context value.
 *
 * @property user - Current authenticated user or null
 * @property isLoading - True while checking authentication status
 * @property isAuthenticated - Convenience boolean for user !== null
 * @property login - Function to authenticate with email/password
 * @property logout - Function to clear authentication state
 * @property refreshUser - Function to re-fetch user data from server
 */
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeAuth: (authData: AuthResponse) => void;
  logout: () => void;
  refreshUser: () => Promise<User | null>;
}

/** React context for authentication state */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Component
 *
 * Wraps the application to provide authentication context to all child
 * components. Automatically hydrates user state from stored JWT token
 * on mount.
 *
 * @param children - React children to render within the provider
 *
 * @example
 * // Wrap your app in the provider
 * export default function RootLayout({ children }) {
 *   return (
 *     <AuthProvider>
 *       {children}
 *     </AuthProvider>
 *   );
 * }
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCheckedAuthRef = useRef(false);
  const justLoggedInRef = useRef(false);
  const userRef = useRef<User | null>(null);
  userRef.current = user;

  const refreshUser = useCallback(async (): Promise<User | null> => {
    // Don't clear loading here - let the caller handle it
    // Set flag to prevent 401 redirects during this operation
    setAuthOperationInProgress(true);
    try {
      const response = await api.get<User>('/auth/me');
      if (response.data) {
        // Success - update user data
        setUser(response.data);
        setAuthOperationInProgress(false);
        return response.data;
      } else if (response.status === 401) {
        // 401 Unauthorized - token is invalid or expired.
        // During initial load, api.ts does not clear the token (isAuthLoading is true), so we must
        // clear it here so the app redirects to login and doesn't keep using an expired token.
        removeAuthToken();
        setUser(null);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
        setAuthOperationInProgress(false);
        return null;
      } else {
        // Other errors (500, network issues, etc.) - don't log out
        // Keep existing user state if we have a token
        const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
        if (!token) {
          // Token was cleared (maybe by api.ts on 401), so clear user too
          setUser(null);
          setAuthOperationInProgress(false);
          return null;
        }
        // Otherwise, keep existing user state - might be a temporary API issue
        setAuthOperationInProgress(false);
        return userRef.current;
      }
    } catch (error) {
      // Network errors or other exceptions - don't log out the user
      // The token might still be valid, just couldn't verify right now
      logger.warn("[AuthProvider] Failed to refresh user:", error);
      setAuthOperationInProgress(false);
      // Don't clear token or user on network errors - keep existing state
      return userRef.current;
    }
  }, []);

  // When any API call gets 401, api.ts clears the token and dispatches this event.
  // Clear user state so the dashboard layout redirects to login (Try Again would never work without re-login).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleSessionExpired = () => {
      setUser(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // Proactive token refresh: call POST /auth/refresh before expiry so session extends without re-login.
  // Run immediately when user is set (e.g. after login or page load with existing token) so we extend
  // the token right away; then every 15 min. This avoids logout when token is already near expiry on load
  // or when the tab was in background (browsers throttle setInterval in background).
  useEffect(() => {
    if (!user) return;
    const refreshToken = async () => {
      const response = await api.post<{ accessToken?: string; access_token?: string }>('/auth/refresh');
      if (!response.error && response.status === 200 && response.data) {
        const token = (response.data as { accessToken?: string; access_token?: string }).accessToken
          ?? (response.data as { accessToken?: string; access_token?: string }).access_token;
        if (token) setAuthToken(token);
      }
      // On 401, api.ts clears token and dispatches auth:session-expired; we clear user above
    };
    refreshToken(); // Run immediately so token is extended on load / login
    const interval = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  // When user returns to the tab, refresh token and profile (permissions/consent may have changed).
  useEffect(() => {
    if (!user || typeof document === 'undefined') return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        api.post<{ accessToken?: string; access_token?: string }>('/auth/refresh').then((response) => {
          if (!response.error && response.status === 200 && response.data) {
            const token = (response.data as { accessToken?: string; access_token?: string }).accessToken
              ?? (response.data as { accessToken?: string; access_token?: string }).access_token;
            if (token) setAuthToken(token);
          }
        });
        void refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [user, refreshUser]);

  useEffect(() => {
    // Only check auth once on mount
    if (hasCheckedAuthRef.current) return;
    
    // Set loading flag to prevent 401 redirects during initial auth check
    setAuthLoading(true);
    
    // Check for existing token on mount
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (token) {
      // If we just logged in, don't refresh (we already have user data)
      if (justLoggedInRef.current) {
        setIsLoading(false);
        hasCheckedAuthRef.current = true;
        justLoggedInRef.current = false; // Reset flag
        setAuthLoading(false);
        return;
      }
      
      // If we already have a user, don't refresh (they just logged in)
      // This prevents unnecessary API calls after login
      if (user) {
        setIsLoading(false);
        hasCheckedAuthRef.current = true;
        setAuthLoading(false);
        return;
      }
      
      // Otherwise, refresh user data from server
      refreshUser().then(() => {
        setIsLoading(false);
        hasCheckedAuthRef.current = true;
        setAuthLoading(false);
      }).catch(() => {
        // Even if refresh fails, mark as checked to prevent loops
        setIsLoading(false);
        hasCheckedAuthRef.current = true;
        setAuthLoading(false);
      });
    } else {
      setIsLoading(false);
      hasCheckedAuthRef.current = true;
      setAuthLoading(false);
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyAuthResponse = useCallback((authData: AuthResponse) => {
    const token = authData.accessToken ?? (authData as { access_token?: string }).access_token;
    const userData = authData.user;

    if (!token) {
      throw new Error('No token received from server');
    }
    if (!userData) {
      throw new Error('No user data received from server');
    }

    setAuthToken(token);
    setUser(userData);
    setIsLoading(false);
    hasCheckedAuthRef.current = true;
    justLoggedInRef.current = true;
    setTimeout(() => {
      setAuthOperationInProgress(false);
    }, 1000);
  }, []);

  const login = async (email: string, password: string) => {
    setAuthOperationInProgress(true);
    try {
      const response = await api.post<AuthResponse>('/auth/login', { email, password }, { auth: false });
      
      if (response.error) {
        setAuthOperationInProgress(false);
        throw new Error(response.error);
      }
      
      if (response.data) {
        applyAuthResponse(response.data);

        if (import.meta.env.DEV) {
          logger.info("[Auth] Login successful", {
            userId: response.data.user?.id,
            userRole: response.data.user?.role,
          });
        }
      } else {
        setAuthOperationInProgress(false);
      }
    } catch (error) {
      setAuthOperationInProgress(false);
      throw error;
    }
  };

  const completeAuth = useCallback((authData: AuthResponse) => {
    setAuthOperationInProgress(true);
    applyAuthResponse(authData);
  }, [applyAuthResponse]);

  const logout = useCallback(() => {
    removeAuthToken();
    setUser(null);
    // Use Next.js router for navigation instead of window.location to avoid full reload
    // Note: This requires the component to have access to router, so we'll use a different approach
    // For now, we'll use window.location but only if we're not already on the home page
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isLoading,
      isAuthenticated: !!user,
      login,
      completeAuth,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context.
 *
 * Provides access to the current user, authentication status, and
 * login/logout functions. Must be used within an AuthProvider.
 *
 * @returns Authentication context with user state and auth functions
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * function ProfilePage() {
 *   const { user, isAuthenticated, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <Redirect to="/login" />;
 *   }
 *
 *   return (
 *     <div>
 *       <h1>Welcome, {user.firstName}!</h1>
 *       <button onClick={logout}>Sign Out</button>
 *     </div>
 *   );
 * }
 *
 * @example
 * // Check user role for authorization
 * function AdminPanel() {
 *   const { user } = useAuth();
 *
 *   if (user?.role !== 'admin') {
 *     return <Unauthorized />;
 *   }
 *
 *   return <AdminDashboard />;
 * }
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
